import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, apiRequest } from '../api/client.js';
import type { ChatHistory, ConnaroEvent, ConnectionEntry, RetentionSummary } from '../api/types.js';
import { useSession } from '../auth/session-context.js';
import { ChatMessage } from '../chat/ChatMessage.js';
import { Composer } from '../chat/Composer.js';
import {
  appendEvent,
  countByPlatform,
  filterEvents,
  mentionsUser,
  type PlatformFilter,
} from '../chat/feed.js';
import { useChatSocket, type ChatConnectionState } from '../chat/use-chat-socket.js';
import { usePreferences } from '../chat/use-preferences.js';
import { Alert } from '../components/Alert.js';
import { EmptyState } from '../components/EmptyState.js';
import { PlatformIcon } from '../components/PlatformIcon.js';
import { StatusPill } from '../components/StatusPill.js';
import type { StatusTone } from '../components/StatusPill.js';
import './chat.css';

/**
 * Connaro Chat (ARCHITECTURE.md §22).
 *
 * A PRIVATE operator-side unified feed. Every message shown here arrived over
 * the authenticated WebSocket from the tenant channel the gateway subscribed us
 * to — there is no sample traffic and no fixture path in this component, per
 * CLAUDE_MASTER_PROMPT.md ("do not fake incoming messages in production").
 *
 * §23 still holds: this is an operator surface, not a public overlay.
 */

/** How the socket's state reads to an operator. */
const SOCKET_PRESENTATION: Record<ChatConnectionState, { tone: StatusTone; label: string }> = {
  idle: { tone: 'idle', label: 'No workspace' },
  connecting: { tone: 'warn', label: 'Connecting' },
  live: { tone: 'ok', label: 'Live' },
  reconnecting: { tone: 'warn', label: 'Reconnecting' },
  unauthorized: { tone: 'error', label: 'Not authorized' },
  signed_out: { tone: 'error', label: 'Session ended' },
  error: { tone: 'error', label: 'Connection error' },
};

/** Distance from the bottom, in pixels, still treated as "at the bottom". */
const AUTOSCROLL_THRESHOLD_PX = 48;

export function ChatPage(): React.JSX.Element {
  const { can, me, activeWorkspace } = useSession();
  const { preferences } = usePreferences();

  /*
   * Read through refs inside the loader: it must see the CURRENT mode without
   * listing preferences as a dependency, which would re-fetch history every
   * time an unrelated setting changed.
   */
  const historyModeRef = useRef(preferences.chatHistory);
  historyModeRef.current = preferences.chatHistory;

  /** Ids that came from history, so they can be dimmed apart from live ones. */
  const [historyIds, setHistoryIds] = useState<ReadonlySet<string>>(new Set());
  const permitted = can('chat.view');

  const workspaceId = permitted ? (activeWorkspace?.id ?? null) : null;
  const socket = useChatSocket(workspaceId);

  const [events, setEvents] = useState<ConnaroEvent[]>([]);
  const [platform, setPlatform] = useState<PlatformFilter>('ALL');
  const [query, setQuery] = useState('');
  const [connections, setConnections] = useState<ConnectionEntry[] | null>(null);
  const [connectionsError, setConnectionsError] = useState<string | null>(null);
  const [retention, setRetention] = useState<RetentionSummary | null>(null);

  const listRef = useRef<HTMLOListElement>(null);
  const [pinnedToBottom, setPinnedToBottom] = useState(true);
  const [unread, setUnread] = useState(0);

  /**
   * Events Connaro Moderator has flagged during THIS session (§98).
   *
   * A live-only marker, deliberately not fetched. The feed shows what is
   * happening now; asking the server which of the last 500 messages produced a
   * finding would be a second query on every load to annotate rows an operator
   * has usually already scrolled past. The Moderator page is where the full
   * queue lives, and this is a pointer towards it.
   */
  const [flaggedEventIds, setFlaggedEventIds] = useState<ReadonlySet<string>>(new Set());

  // --- ingest ---------------------------------------------------------------

  useEffect(
    () =>
      socket.onFinding((finding) => {
        setFlaggedEventIds((current) => {
          if (current.has(finding.eventId)) return current;
          const next = new Set(current);
          next.add(finding.eventId);
          return next;
        });
      }),
    [socket],
  );

  useEffect(() => {
    return socket.onEvent((event) => {
      setEvents((current) => appendEvent(current, event));
    });
  }, [socket]);

  /*
   * Switching workspace clears the feed.
   *
   * §13 requires that cross-workspace state not remain visible after a switch.
   * The gateway already stops delivering the old tenant's events; leaving them
   * on screen would defeat the point.
   */
  useEffect(() => {
    setEvents([]);
    setUnread(0);
    setRetention(null);
  }, [workspaceId]);

  /*
   * Load retained history (§31, §66).
   *
   * Seeded through the same `appendEvent` the socket uses, so ordering and
   * deduplication are identical whichever way a message arrived — and a message
   * delivered live while this request was in flight is not shown twice.
   *
   * An empty result is the NORMAL case on a plan without retention, so a
   * failure here is reported quietly and never blocks the live feed.
   */
  useEffect(() => {
    if (!permitted || workspaceId === null) return;

    /*
     * Read through a ref rather than a plain boolean.
     *
     * TypeScript cannot see the cleanup function running during the `await`, so
     * it narrows an inline `cancelled` check to a constant and the guard is
     * reported as dead code. It is not: unmounting or switching workspace
     * mid-request is exactly what it exists for.
     */
    const state = { cancelled: false };

    void (async () => {
      try {
        const history = await apiRequest<ChatHistory>('/chat/messages?limit=200');
        if (state.cancelled) return;

        setRetention(history.retention);

        /*
         * `chatHistory` decides what the operator is SHOWN, not what Connaro
         * stored — retention decides that (§31). The request is made either
         * way, because its `retention` half drives the plan notice below, and
         * suppressing it would hide the reason the feed is empty.
         *
         * `live_only` and `off` differ in intent rather than in effect today:
         * neither seeds history, and `live_only` is the one that will keep
         * showing messages that arrive while watching once a future mode can
         * clear them. Both are honoured by simply not seeding.
         */
        if (historyModeRef.current === 'all') {
          const seeded = new Set(history.messages.map((event) => event.id));
          setHistoryIds(seeded);
          setEvents((current) =>
            history.messages.reduce((acc, event) => appendEvent(acc, event), current),
          );
        }
      } catch {
        // History is a convenience; live chat is the product.
      }
    })();

    return () => {
      state.cancelled = true;
    };
  }, [permitted, workspaceId]);

  // --- connection status ----------------------------------------------------

  useEffect(() => {
    if (!permitted) return;

    void (async () => {
      try {
        const response = await apiRequest<{ connections: ConnectionEntry[] }>('/connections');
        setConnections(response.connections);
      } catch (caught) {
        setConnectionsError(
          caught instanceof ApiError ? caught.message : 'Could not load connection status.',
        );
      }
    })();
  }, [permitted, workspaceId]);

  // --- derived --------------------------------------------------------------

  const mentionHandle = me?.user.displayName;

  const visible = useMemo(
    () =>
      /*
       * Event-type filtering happens HERE rather than in `filterEvents`, which
       * is shared with the dock: a broadcast overlay and an operator's console
       * are different audiences, and a type hidden from one is not necessarily
       * hidden from the other.
       */
      filterEvents(events, { platform, query }).filter(
        (event) => !preferences.hiddenEventTypes.includes(event.type),
      ),
    [events, platform, query, preferences.hiddenEventTypes],
  );

  const counts = useMemo(() => countByPlatform(events), [events]);

  /**
   * Platforms offered as filters.
   *
   * Those this workspace actually has a CONNECTION to — not those the catalog
   * calls available. Filtering by a platform you are receiving from is not a
   * claim of support (§18), it is a view over traffic that has already
   * arrived; and keying off the catalog meant a validated-but-unlisted platform
   * delivered messages with no way to filter them.
   *
   * A workspace with nothing connected sees only ALL, which is honest: there is
   * nothing to filter.
   */
  const availablePlatforms = useMemo(
    () => (connections ?? []).filter((entry) => entry.connection !== null),
    [connections],
  );

  const connected = useMemo(
    () => (connections ?? []).filter((entry) => entry.connection !== null),
    [connections],
  );

  const needsAttention = connected.filter(
    (entry) => entry.connection !== null && entry.connection.tone !== 'ok',
  );

  // --- auto-scroll ----------------------------------------------------------

  const handleScroll = useCallback(() => {
    const list = listRef.current;
    if (list === null) return;

    const distance = list.scrollHeight - list.scrollTop - list.clientHeight;
    const atBottom = distance <= AUTOSCROLL_THRESHOLD_PX;

    setPinnedToBottom(atBottom);
    if (atBottom) setUnread(0);
  }, []);

  /*
   * Follow the feed only while the operator is already at the bottom.
   *
   * Yanking the view down while they are reading back is the single most
   * irritating thing a live feed can do, so scrolling away pauses the follow
   * and the count below tells them what they are missing.
   *
   * useLayoutEffect, not useEffect: this must run before the browser paints,
   * or the new row is visible at the old scroll position for one frame.
   */
  useLayoutEffect(() => {
    const list = listRef.current;
    if (list === null) return;

    if (pinnedToBottom) {
      list.scrollTop = list.scrollHeight;
    } else {
      setUnread((count) => count + 1);
    }
    // Only when the rendered set actually grows.
  }, [visible.length, pinnedToBottom]);

  const jumpToLatest = useCallback(() => {
    const list = listRef.current;
    if (list === null) return;

    list.scrollTop = list.scrollHeight;
    setPinnedToBottom(true);
    setUnread(0);
  }, []);

  // --- render ---------------------------------------------------------------

  if (!permitted) {
    return (
      <section className="panel">
        <div className="panel__body">
          <EmptyState title="You do not have access to chat">
            Ask a workspace administrator for the operator role.
          </EmptyState>
        </div>
      </section>
    );
  }

  const status = SOCKET_PRESENTATION[socket.state];

  return (
    <>
      <header className="page__header">
        <h1 className="page__title">Chat</h1>
        <p className="page__subtitle">
          Private operator view of your live chat across every connected platform.
        </p>
      </header>

      <section className="panel chat" aria-labelledby="chat-heading">
        <div className="panel__header">
          <h2 className="panel__title" id="chat-heading">
            Unified feed
          </h2>
          <StatusPill tone={status.tone} label={status.label} />
        </div>

        {socket.message !== null && (
          <div className="panel__body">
            <Alert tone={socket.state === 'signed_out' ? 'error' : 'warning'}>
              {socket.message}{' '}
              {socket.state === 'error' || socket.state === 'reconnecting' ? (
                <button type="button" className="btn btn--link" onClick={socket.reconnectNow}>
                  Retry now
                </button>
              ) : null}
            </Alert>
          </div>
        )}

        {connectionsError !== null && (
          <div className="panel__body">
            <Alert tone="warning">{connectionsError}</Alert>
          </div>
        )}

        {needsAttention.map((entry) => (
          <div className="panel__body" key={entry.platform}>
            <Alert tone={entry.connection?.tone === 'error' ? 'error' : 'warning'}>
              {entry.displayName}: {entry.connection?.label}
              {entry.connection?.lastErrorMessage !== null &&
              entry.connection?.lastErrorMessage !== undefined
                ? ` — ${entry.connection.lastErrorMessage}`
                : ''}{' '}
              <Link to="/connections">Manage connections</Link>
            </Alert>
          </div>
        ))}

        <div className="panel__body chat__controls">
          <div className="chat__filters" role="group" aria-label="Filter by platform">
            <button
              type="button"
              className={`btn btn--filter${platform === 'ALL' ? ' is-active' : ''}`}
              aria-pressed={platform === 'ALL'}
              onClick={() => {
                setPlatform('ALL');
              }}
            >
              All
              <span className="btn__count">{events.length}</span>
            </button>

            {availablePlatforms.map((entry) => (
              <button
                key={entry.platform}
                type="button"
                className={`btn btn--filter${platform === entry.platform ? ' is-active' : ''}`}
                // Carries the platform's hue in CSS. The button still says its
                // name and sets aria-pressed, so colour adds nothing it needs.
                data-platform={entry.platform}
                aria-pressed={platform === entry.platform}
                onClick={() => {
                  setPlatform(entry.platform);
                }}
              >
                <PlatformIcon platform={entry.platform} displayName={entry.displayName} />
                {entry.displayName}
                <span className="btn__count">{counts[entry.platform] ?? 0}</span>
              </button>
            ))}
          </div>

          <div className="chat__search">
            <label className="visually-hidden" htmlFor="chat-search">
              Search the live feed
            </label>
            <input
              id="chat-search"
              type="search"
              className="field__input chat__search-input"
              placeholder="Search messages and names…"
              value={query}
              onChange={(changed) => {
                setQuery(changed.target.value);
              }}
            />
          </div>
        </div>

        <div className="chat__viewport">
          <ol
            /*
             * Size and striping are applied ONCE at the list, not per row.
             * Every row inherits `font-size`, and the stripe is a CSS
             * `:nth-child` rule — pushing either into ChatMessage would put a
             * style recalculation on every one of several hundred rows.
             */
            style={{
              fontSize: `${String(preferences.fontSize)}px`,
              ...(preferences.fadeOutSeconds === 0
                ? {}
                : { '--fade-after': `${String(preferences.fadeOutSeconds)}s` }),
            }}
            className={`chat__list${preferences.alternatingBackgrounds ? ' chat__list--striped' : ''}${
              preferences.fadeOutSeconds === 0 ? '' : ' chat__list--fading'
            }`}
            ref={listRef}
            onScroll={handleScroll}
            aria-label="Live chat messages"
            aria-live="polite"
            aria-relevant="additions"
          >
            {visible.map((event) => (
              <ChatMessage
                key={event.id}
                event={event}
                mentionHandle={mentionHandle}
                mentioned={preferences.highlightMentions && mentionsUser(event, mentionHandle)}
                fromHistory={preferences.dimChatHistory && historyIds.has(event.id)}
                timeZone={undefined}
                /*
                 * The Moderator bridge (§98).
                 *
                 * Offered only to an actor who can open the page. `can` is a
                 * rendering convenience, never the boundary — Moderator's own
                 * routes authorise every request regardless (§4.2).
                 */
                moderation={can('moderation.view')}
                flagged={flaggedEventIds.has(event.id)}
              />
            ))}
          </ol>

          {!pinnedToBottom && unread > 0 && (
            <button type="button" className="chat__jump" onClick={jumpToLatest}>
              {unread} new {unread === 1 ? 'message' : 'messages'} — jump to latest
            </button>
          )}
        </div>

        {visible.length === 0 && (
          <div className="panel__body">
            {events.length > 0 ? (
              <EmptyState title="No messages match">
                Nothing in the live feed matches this filter or search.
              </EmptyState>
            ) : connected.length === 0 ? (
              /* Nothing connected is the more actionable problem, so it is
                 reported before any explanation about retention. */
              <EmptyState title="No messages yet">
                Connaro is not connected to any streaming platform, so there is nothing to show.
                Connect one on the <Link to="/connections">Connections</Link> page.
              </EmptyState>
            ) : retention?.enabled === false ? (
              <EmptyState title="Waiting for messages">
                Messages appear here as they are sent. This workspace is on the{' '}
                <strong>{retention.plan}</strong> plan, which does not retain chat history — the
                feed starts empty each time you open it.
              </EmptyState>
            ) : (
              <EmptyState title="Waiting for messages">
                Connaro is connected and listening. Messages appear here as they are sent.
              </EmptyState>
            )}
          </div>
        )}
      </section>

      {/*
       * Speaking, under the feed the operator is reading.
       *
       * Renders nothing at all for an actor without `chat.send` — the composer
       * decides that from the session itself, so it is absent rather than
       * disabled wherever it is mounted. A sent message is NOT echoed into the
       * list above: every platform sends the operator's own message back
       * through ingestion, so the feed shows what the platform actually
       * accepted rather than what the Console hoped for.
       */}
      <Composer />
    </>
  );
}
